import { styled } from "goober";
import {
  Fragment,
  type ReactNode,
  type RefObject,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { caretCoordinates } from "../../lib/caretCoordinates";
import { zIndexes } from "../../lib/zIndexes";
import { PressButton } from "../PressButton";
import { Text } from "../Text";

const GAP = 6;
const VIEWPORT_PAD = 8;

const Popover = styled("div")`
  position: fixed;
  z-index: ${zIndexes.dropdown};
  width: 160px;
  max-height: 200px;
  overflow-y: auto;
  padding: 4px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 1px;
  background: var(--surface-primary);
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 12px;
`;

const Row = styled(PressButton)`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  height: 24px;
  flex-shrink: 0;
  padding: 0 8px;
  border-radius: 8px;
  background: transparent;
  color: var(--text-primary);
  &:hover {
    background: var(--alpha4);
  }
  &[data-active="true"] {
    background: var(--alpha4);
  }
`;

const IconSlot = styled("span")`
  display: inline-flex;
  flex-shrink: 0;
`;

const SectionHeader = styled("div")`
  display: flex;
  align-items: center;
  height: 24px;
  flex-shrink: 0;
  padding: 0 8px;
  color: var(--text-secondary);
`;

const Footer = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  flex-shrink: 0;
  padding: 0 8px;
  color: var(--text-secondary);
`;

export type CaretMenuItem = {
  disabled?: boolean;
  id: string;
  icon: ReactNode;
  label: string;
  section?: string;
};

type Props = {
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  slashStart: number;
  items: readonly CaretMenuItem[];
  selectedIndex: number;
  onSelect: (id: string) => void;
  footer?: ReactNode;
};

export function CaretMenu({
  textareaRef,
  value,
  slashStart,
  items,
  selectedIndex,
  onSelect,
  footer,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const ta = textareaRef.current;
    const el = ref.current;
    if (!ta || !el) return;
    let raf = 0;
    const place = () => {
      raf = 0;
      const taRect = ta.getBoundingClientRect();
      const caret = caretCoordinates(ta, slashStart);
      const caretLeft = taRect.left + caret.left - ta.scrollLeft;
      const caretTop = taRect.top + caret.top - ta.scrollTop;
      const menuWidth = el.offsetWidth;
      const menuHeight = el.offsetHeight;
      const placeAbove = caretTop - VIEWPORT_PAD >= menuHeight + GAP;
      const top = placeAbove
        ? caretTop - menuHeight - GAP
        : caretTop + caret.height + GAP;
      const maxLeft = window.innerWidth - menuWidth - VIEWPORT_PAD;
      setPos({
        left: Math.max(VIEWPORT_PAD, Math.min(caretLeft, maxLeft)),
        top: Math.max(
          VIEWPORT_PAD,
          Math.min(top, window.innerHeight - menuHeight - VIEWPORT_PAD)
        ),
      });
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(place);
    };
    place();
    const ro = new ResizeObserver(schedule);
    ro.observe(ta);
    ro.observe(el);
    ta.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      ro.disconnect();
      ta.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [textareaRef, value, slashStart, items.length]);

  useLayoutEffect(() => {
    ref.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  const showHeaders =
    items.every((i) => i.section !== undefined) &&
    new Set(items.map((i) => i.section)).size > 1;

  return createPortal(
    <Popover
      ref={ref}
      data-popover
      style={{
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        opacity: pos ? 1 : 0,
      }}
      // Keep the textarea focused so selecting a row doesn't dismiss the menu.
      onMouseDown={(e) => e.preventDefault()}
    >
      {items.map((item, index) => {
        const header =
          showHeaders &&
          (index === 0 || items[index - 1]!.section !== item.section);
        return (
          <Fragment key={item.id}>
            {header && (
              <SectionHeader>
                <Text variant="text-small">{item.section}</Text>
              </SectionHeader>
            )}
            <Row
              disabled={item.disabled}
              data-no-pill=""
              data-active={index === selectedIndex ? "true" : undefined}
              onPress={() => onSelect(item.id)}
            >
              <IconSlot>{item.icon}</IconSlot>
              <Text
                variant="text-small-md"
                color="var(--text-primary)"
                ellipsis
              >
                {item.label}
              </Text>
            </Row>
          </Fragment>
        );
      })}
      {footer !== undefined && <Footer>{footer}</Footer>}
    </Popover>,
    // Form scroll-dismiss uses transform, which would retarget fixed layout if we
    // portaled into a host under the form.
    document.body
  );
}
